import { Button, Checkbox, DatePicker, Drawer, Form, Input, message, Select } from 'antd';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getUserEmailAddress } from '../../store/user/user-reducer';
import { fetchLocationsForRequestor, fetchRequestors, SaveHooAccommodation } from './actions';
import { getAllRequestorData, getHoursOptions, getLocations, getReasons, getRequestors } from './selectors';

interface AddHooRequestDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: (values: any) => void;
  loading?: boolean;
  modalData?: any;
}

const { Option } = Select;
const { TextArea } = Input;

const dayFields = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const convertTo24Hour = (timeStr: string): string => {
  const [time, meridiem] = timeStr.trim().split(' ');
  let [hour, minute] = time.split(':').map(Number);

  if (meridiem.toUpperCase() === 'PM' && hour < 12) hour += 12;
  if (meridiem.toUpperCase() === 'AM' && hour === 12) hour = 0;

  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
};

const AddHooRequestDrawer: React.FC<AddHooRequestDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
  modalData = null,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const requestors = useSelector(getRequestors);
  const locations = useSelector(getLocations);
  const reasons = useSelector(getReasons);
  const hourOptions = useSelector(getHoursOptions);
  const allRequestorData = useSelector(getAllRequestorData);
  const userEmailAddress = useSelector(getUserEmailAddress);
  const [templateImpactChecked, setTemplateImpactChecked] = React.useState(false);

  const handleFormClose = (isSubmit?: boolean) => {
    form.resetFields();
    setTemplateImpactChecked(false);
    if (!isSubmit && onClose) {
      onClose();
    } else if (isSubmit && onSubmit) {
      onSubmit('');
    }
  };
  const handleSubmit = (values) => {
    try {
      if (
        values &&
        !values.monday &&
        !values.tuesday &&
        !values.wednesday &&
        !values.thursday &&
        !values.friday &&
        !values.saturday &&
        !values.sunday
      ) {
        message.error('Please select hours for at least one day!');
        return;
      }
      if (onSubmit) {
        const details = dayFields
          .map((dayKey) => {
            const value = values[dayKey.toLowerCase()];
            if (!value) return null; // Skip if undefined or null
            else if (value.toUpperCase() === 'CLOSED')
              return {
                DayOfWeek: dayKey.charAt(0).toUpperCase() + dayKey.slice(1),
                OpenTime: '00:00',
                CloseTime: '00:00',
              };

            const [openStr, closeStr] = value.split('-');
            return {
              DayOfWeek: dayKey.charAt(0).toUpperCase() + dayKey.slice(1),
              OpenTime: convertTo24Hour(openStr),
              CloseTime: convertTo24Hour(closeStr),
            };
          })
          .filter(Boolean); // Remove nulls

        const payload = {
          ScheduleChangeRequestId: modalData?.ScheduleChangeRequestId || null,
          RequestorId: values.requestorId,
          LocationIds: values.locationIds,
          EffectiveDate: values.effectiveDate ? values.effectiveDate.toISOString().split('T')[0] : null,
          Reason: values.reason,
          Notes: values.notes,
          Details: details,
        };
        dispatch(
          SaveHooAccommodation({
            ...payload,
            onSuccess: () => {
              handleFormClose(true);
            },
          })
        );
      }
    } catch (error) {
      console.error('Error fetching current pattern:', error);
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(fetchRequestors('RD,RCD,RCFD,AV'));
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (open && modalData) {
      const initialValues = {
        scheduleChangeRequestId: modalData.ScheduleChangeRequestId || undefined,
        requestorId: modalData.RequestorId || undefined,
        locationIds: modalData.LocationId ? [modalData.LocationId] : [],
        effectiveDate: modalData.EffectiveDate ? moment(modalData.EffectiveDate) : undefined,
        reason: modalData.Reason || undefined,
        notes: modalData.Notes || '',
        doctorTemplatePatternImpact: true,
        monday: modalData.Monday || undefined,
        tuesday: modalData.Tuesday || undefined,
        wednesday: modalData.Wednesday || undefined,
        thursday: modalData.Thursday || undefined,
        friday: modalData.Friday || undefined,
        saturday: modalData.Saturday || undefined,
        sunday: modalData.Sunday || undefined,
      };
      setTemplateImpactChecked(true);
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [open, modalData]);

  useEffect(() => {
    if (open && userEmailAddress && requestors.length > 0 && modalData == null) {
      const matchedRequestor = requestors.find(
        (r) => r.contact?.ContactEmail?.toLowerCase() === userEmailAddress.toLowerCase()
      );
      if (matchedRequestor) {
        form.setFieldsValue({ requestorId: matchedRequestor.value });
        dispatch(fetchLocationsForRequestor(matchedRequestor.value, allRequestorData));
      }
    } else if (open && modalData != null) {
      dispatch(fetchLocationsForRequestor(modalData.RequestorId, allRequestorData));
    }
  }, [open, userEmailAddress, requestors, modalData]);

  const handleRequestorChange = (contactSid: number) => {
    form.setFieldsValue({ locationIds: [] });
    dispatch(fetchLocationsForRequestor(contactSid, allRequestorData));
  };

  return (
    <Drawer
      title={modalData ? 'Edit HOO Accommodation Request' : 'Add New HOO Accommodation Request'}
      closable={false}
      width={480}
      open={open}
      destroyOnClose
      footer={
        <div style={{ textAlign: 'right' }}>
          <Button
            onClick={() => {
              handleFormClose(false);
            }}
            style={{ marginRight: 8 }}
          >
            Cancel
          </Button>
          <Button
            type='primary'
            onClick={() => {
              console.log('submitting form', JSON.stringify(form.getFieldsValue()));
              form.submit();
            }}
            loading={loading}
            disabled={!loading && templateImpactChecked === false}
          >
            Submit
          </Button>
        </div>
      }
    >
      <Form form={form} layout='vertical' onFinish={handleSubmit} initialValues={{ locationIds: [] }}>
        <Form.Item
          label='Name of Requestor (RD/RCD/AV)'
          name='requestorId'
          rules={[{ required: true, message: 'Requestor is required' }]}
        >
          <Select
            showSearch
            disabled={modalData != null && modalData.RequestorId != null}
            placeholder='Type to search requestor'
            onChange={handleRequestorChange}
            filterOption={(input, option) =>
              option?.children ? option.children.toString().toLowerCase().includes(input.toLowerCase()) : false
            }
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {requestors &&
              requestors.map((r) => (
                <Option key={r.value} value={r.value}>
                  {r.label}
                </Option>
              ))}
          </Select>
        </Form.Item>

        <Form.Item
          label='Location'
          name='locationIds'
          rules={[{ required: true, message: 'At least one location is required' }]}
        >
          <Select
            mode='multiple'
            placeholder='Select location(s)'
            disabled={!form.getFieldValue('requestorId') || (modalData != null && modalData.LocationId != null)}
            filterOption={(input, option) => {
              const label = (option?.label ?? option?.children ?? '').toString().toLowerCase();
              return label.includes(input.toLowerCase());
            }}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {locations &&
              locations.map((loc) => (
                <Option key={loc.value} value={loc.value}>
                  {loc.label}
                </Option>
              ))}
          </Select>
        </Form.Item>

        {dayFields &&
          dayFields.map((opt) => (
            <Form.Item key={`ele-${opt}`} label={`Proposed New ${opt} Hours`} name={opt.toLowerCase()}>
              <Select
                placeholder='Select hours'
                allowClear
                showSearch
                filterOption={(input, option) => {
                  const label = (option?.label ?? option?.children ?? '').toString().toLowerCase();
                  return label.includes(input.toLowerCase());
                }}
                getPopupContainer={(trigger) => trigger.parentNode}
              >
                {hourOptions &&
                  hourOptions.map((opt) => (
                    <Option key={opt} value={opt}>
                      {opt}
                    </Option>
                  ))}
              </Select>
            </Form.Item>
          ))}

        <Form.Item
          label='Doctor Template Pattern Impact'
          name='doctorTemplatePatternImpact'
          valuePropName='checked'
          style={{ display: 'flex', alignItems: 'center' }}
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          rules={[{ required: true, message: 'Please accept the doctor template pattern impact' }]}
        >
          <Checkbox
            onChange={(e) => {
              setTemplateImpactChecked(e.target.checked);
            }}
          />
        </Form.Item>

        <Form.Item
          label='Requested Effective Date for Change'
          name='effectiveDate'
          rules={[{ required: true, message: 'Please select start date' }]}
        >
          <DatePicker
            format={'YYYY-MM-DD'}
            getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
            disabledDate={(d) => {
              const dayAfterTomorrow = moment().add(2, 'days').startOf('day');
              return d && d < dayAfterTomorrow;
            }}
            inputReadOnly
          />
        </Form.Item>
        <Form.Item
          label='Request Reason'
          name='reason'
          rules={[{ required: true, message: 'Request reason is required' }]}
        >
          <Select
            placeholder='Select request reason'
            allowClear
            showSearch
            filterOption={(input, option) => {
              const label = (option?.label ?? option?.children ?? '').toString().toLowerCase();
              return label.includes(input.toLowerCase());
            }}
            getPopupContainer={(trigger) => trigger.parentNode}
          >
            {reasons &&
              reasons.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
          </Select>
        </Form.Item>
        <Form.Item
          label='Reason for the request'
          name='notes'
          rules={[
            {
              validator: (_, value) => {
                if (form.getFieldValue('reason')?.includes('Other') && (!value || !value.trim())) {
                  return Promise.reject(new Error('Please enter notes if "Other" is selected'));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <TextArea rows={3} maxLength={4000} placeholder='Enter reason...' />
        </Form.Item>
      </Form>
    </Drawer>
  );
};

export default AddHooRequestDrawer;
